// Authentication service for login, logout, and session management
import Cookies from 'js-cookie';
import socketService from './socketService';

export interface LogoutOptions {
  clearLocalStorage?: boolean;
  clearSessionStorage?: boolean;
  clearIndexedDB?: boolean;
  redirectTo?: string;
  notifyBackend?: boolean;
}

export class AuthService {
  private static readonly TOKEN_KEY = 'crm_auth_token';
  private static readonly USER_DATA_KEY = 'crm_user_data';
  private static readonly API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

  /**
   * Modern logout implementation with comprehensive cleanup
   */
  static async logout(options: LogoutOptions = {}): Promise<void> {
    const {
      clearLocalStorage = true,
      clearSessionStorage = true,
      clearIndexedDB = true,
      redirectTo = import.meta.env.VITE_LOGOUT_REDIRECT_URL || '/login',
      notifyBackend = true
    } = options;

    try {
      // 0. Disconnect socket connection first
      socketService.disconnect();

      // 1. Notify backend to invalidate token (optional blacklisting)
      if (notifyBackend) {
        const token = Cookies.get(this.TOKEN_KEY);
        if (token) {
          try {
            await fetch(`${this.API_URL}/auth/logout`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              }
            });
          } catch (error) {
            console.error('Failed to notify backend about logout:', error);
            // Continue with client-side cleanup even if backend call fails
          }
        }
      }

      // 2. Clear all cookies (including httpOnly if possible)
      this.clearAllCookies();

      // 3. Clear localStorage
      if (clearLocalStorage) {
        this.clearLocalStorage();
      }

      // 4. Clear sessionStorage
      if (clearSessionStorage) {
        this.clearSessionStorage();
      }

      // 5. Clear IndexedDB (if used)
      if (clearIndexedDB) {
        await this.clearIndexedDB();
      }

      // 6. Clear any in-memory caches or states
      this.clearMemoryCache();

      // 7. Revoke any service worker registrations
      await this.revokeServiceWorkers();

      // 8. Redirect to login page
      if (redirectTo) {
        window.location.href = redirectTo;
      }

    } catch (error) {
      console.error('Error during logout:', error);
      // Force redirect even on error
      if (redirectTo) {
        window.location.href = redirectTo;
      }
    }
  }

  /**
   * Clear all cookies for the current domain
   */
  private static clearAllCookies(): void {
    // List of all known cookies to clear
    const cookiesToClear = [
      'crm_auth_token',
      'crm_session_id',
      'token',
      'user_info',
      'social_plan_details'
    ];

    // Get cookie domain from environment or detect from hostname
    const hostname = window.location.hostname;
    const cookieDomain = hostname.includes('yellowslate.com') ? '.yellowslate.com' :
                        hostname.includes('localhost') ? 'localhost' :
                        hostname;

    // Clear specific known cookies with all possible domain/path combinations
    cookiesToClear.forEach(cookieName => {
      // Try js-cookie removal
      Cookies.remove(cookieName);
      Cookies.remove(cookieName, { path: '/' });
      Cookies.remove(cookieName, { path: '/', domain: cookieDomain });
      if (cookieDomain.startsWith('.')) {
        Cookies.remove(cookieName, { path: '/', domain: cookieDomain.substring(1) });
      }

      // Try native removal with different domain variations
      document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${cookieDomain}`;
      if (cookieDomain.startsWith('.')) {
        document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${cookieDomain.substring(1)}`;
      }
    });

    // Also clear any remaining cookies
    document.cookie.split(';').forEach(cookie => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();

      if (name && !cookiesToClear.includes(name)) {
        Cookies.remove(name);
        Cookies.remove(name, { path: '/' });
        Cookies.remove(name, { path: '/', domain: cookieDomain });
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${cookieDomain}`;
      }
    });
  }

  /**
   * Clear localStorage with CRM-specific keys
   */
  private static clearLocalStorage(): void {
    // List of all known localStorage keys to explicitly clear (keeping UI preferences)
    const keysToExplicitlyClear = [
      'crm_user_data',
      'crm_user',
      'crm_auth_token',
      'crm_user_column_preferences',
      'crm_tour_leads_auto_started',
      'crm_tour_pipeline_auto_started',
      'crm_tour_leads_completed',
      'crm_tour_pipeline_completed',
      'crm_previous_account', // Clear previous account marker on logout
      'user',
      'token'
    ];

    // First, explicitly clear all known auth-related keys
    keysToExplicitlyClear.forEach(key => {
      localStorage.removeItem(key);
    });

    // Then clear all remaining keys (no keys preserved on logout)
    Object.keys(localStorage).forEach(key => {
      if (!keysToExplicitlyClear.includes(key)) {
        localStorage.removeItem(key);
      }
    });
  }

  /**
   * Clear sessionStorage
   */
  private static clearSessionStorage(): void {
    sessionStorage.clear();
  }

  /**
   * Clear IndexedDB databases
   */
  private static async clearIndexedDB(): Promise<void> {
    if ('indexedDB' in window) {
      try {
        const databases = await indexedDB.databases();
        const clearPromises = databases.map(db => {
          if (db.name) {
            return indexedDB.deleteDatabase(db.name);
          }
        });
        await Promise.all(clearPromises);
      } catch (error) {
        console.error('Failed to clear IndexedDB:', error);
      }
    }
  }

  /**
   * Clear any in-memory caches
   */
  private static clearMemoryCache(): void {
    // Clear React Query cache if using it
    if ((window as any).queryClient) {
      (window as any).queryClient.clear();
    }

    // Clear any Redux store
    if ((window as any).store) {
      (window as any).store.dispatch({ type: 'LOGOUT' });
    }

    // Clear any other in-memory stores
    if ((window as any).__APP_CACHE__) {
      (window as any).__APP_CACHE__ = {};
    }
  }

  /**
   * Unregister service workers
   */
  private static async revokeServiceWorkers(): Promise<void> {
    if ('serviceWorker' in navigator) {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        const unregisterPromises = registrations.map(registration =>
          registration.unregister()
        );
        await Promise.all(unregisterPromises);
      } catch (error) {
        console.error('Failed to unregister service workers:', error);
      }
    }
  }

  /**
   * Check if user is authenticated
   */
  static isAuthenticated(): boolean {
    return !!Cookies.get(this.TOKEN_KEY);
  }

  /**
   * Get current auth token
   */
  static getToken(): string | undefined {
    return Cookies.get(this.TOKEN_KEY);
  }

  /**
   * Set auth token
   */
  static setToken(token: string, options?: Cookies.CookieAttributes): void {
    // Get cookie domain - use .yellowslate.com for production to work across subdomains
    const hostname = window.location.hostname;
    const cookieDomain = hostname.includes('yellowslate.com') ? '.yellowslate.com' :
                        hostname.includes('localhost') ? undefined : // localhost doesn't support dot notation
                        undefined; // For other environments, use default domain

    const defaultOptions: Cookies.CookieAttributes = {
      expires: 1, // 1 day
      secure: window.location.protocol === 'https:',
      sameSite: 'lax', // Changed from 'strict' to 'lax' for better cross-subdomain support
      path: '/',
      domain: cookieDomain // Add domain for cross-subdomain support
    };

    console.log('🍪 Setting auth token cookie:', {
      hostname,
      domain: cookieDomain,
      secure: window.location.protocol === 'https:'
    });

    Cookies.set(this.TOKEN_KEY, token, { ...defaultOptions, ...options });
  }

  /**
   * Store user data in localStorage
   */
  static setUserData(userData: any): void {
    localStorage.setItem(this.USER_DATA_KEY, JSON.stringify(userData));
  }

  /**
   * Get user data from localStorage
   */
  static getUserData(): any {
    const data = localStorage.getItem(this.USER_DATA_KEY);
    return data ? JSON.parse(data) : null;
  }

  /**
   * Alias for getUserData() for backward compatibility
   * @deprecated Use getUserData() instead
   */
  static getUser(): any {
    return this.getUserData();
  }
}

export default AuthService;